import type { ClientLink } from '@campfire/net';

export interface SocketLink extends ClientLink {
  /** 'denied': the host refused this room code; retrying cannot help, so the link stops. */
  readonly status: 'connecting' | 'open' | 'closed' | 'denied';
  /** Failed attempts since the last successful open. */
  readonly failures: number;
  onStatus(cb: (s: SocketLink['status']) => void): void;
  /** Called after every (re)open so the app can re-send hello with its token. */
  onOpen(cb: () => void): void;
  close(): void;
}

/**
 * WebSocket ClientLink with exponential-backoff reconnect (phones sleep, Wi-Fi blips).
 * Browsers hide why an upgrade failed, so after a close `probe` asks the host over HTTP
 * whether the room code is still valid; a refusal stops the retry loop.
 */
export function connectSocket(url: string, probe: () => Promise<boolean> = async () => true, protocols: string[] = []): SocketLink {
  const msgCbs: ((d: string) => void)[] = [];
  const statusCbs: ((s: SocketLink['status']) => void)[] = [];
  const openCbs: (() => void)[] = [];
  let ws: WebSocket | null = null;
  let status: SocketLink['status'] = 'connecting';
  let attempt = 0;
  let stopped = false;
  const queue: string[] = [];

  const setStatus = (s: SocketLink['status']) => {
    status = s;
    statusCbs.forEach((cb) => cb(s));
  };

  const open = () => {
    setStatus('connecting');
    ws = new WebSocket(url, protocols);
    ws.onopen = () => {
      attempt = 0;
      setStatus('open');
      openCbs.forEach((cb) => cb());
      while (queue.length) ws?.send(queue.shift() as string);
    };
    ws.onmessage = (e) => msgCbs.forEach((cb) => cb(String(e.data)));
    ws.onclose = () => {
      setStatus('closed');
      if (stopped) return;
      const delay = Math.min(5000, 250 * 2 ** attempt++);
      void probe()
        .catch(() => true)
        .then((allowed) => {
          if (stopped) return;
          if (!allowed) {
            stopped = true;
            setStatus('denied');
            return;
          }
          setTimeout(open, delay);
        });
    };
  };

  // Reconnect promptly when the phone wakes up or the tab returns to the foreground.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && status === 'closed' && !stopped) open();
  });

  open();
  return {
    get status() {
      return status;
    },
    get failures() {
      return attempt;
    },
    send(data) {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
      else queue.push(data);
    },
    onMessage: (cb) => msgCbs.push(cb),
    onStatus: (cb) => statusCbs.push(cb),
    onOpen: (cb) => openCbs.push(cb),
    close() {
      stopped = true;
      ws?.close();
    },
  };
}
